// browserTools.ts — pure-fetch implementations of all 10 portfolio tools.
// These run in the browser, no backend involved. Each exposes an
// MCP-compliant inputSchema and an async call() returning structured data.
//
// Why duplicate the server-side tools in api/tools/?
//   The server-side versions live on Vercel edge and use process.env (e.g.
//   SUPABASE_SERVICE_ROLE_KEY). The browser path needs versions that work
//   with public APIs only. Where logic is identical we keep the shapes
//   consistent.
//
// Architecture: this file knows nothing about MCP — it just defines tools.
// mcpTransport.ts wraps these in the JSON-RPC envelope.

export type MCPInputSchema = {
  type: "object";
  properties: Record<string, { type: string; description: string; enum?: string[] }>;
  required: string[];
};

export type ToolResult = {
  ok: boolean;
  data?: unknown;
  error?: string;
};

export type MCPTool = {
  name: string;
  description: string;
  inputSchema: MCPInputSchema;
  call: (args: Record<string, unknown>) => Promise<ToolResult>;
};

// ─────────────────────────── individual tools ───────────────────────────

const getGithubRepo: MCPTool = {
  name: "getGithubRepo",
  description:
    "Fetch live data for a GitHub repository (stars, forks, latest push, primary language, description). Use whenever the user asks about a specific repo.",
  inputSchema: {
    type: "object",
    properties: {
      owner: { type: "string", description: "Repo owner. e.g. josharsh, reallyartificial." },
      repo: { type: "string", description: "Repo name. e.g. mcp-jest, 100linesofCode." },
    },
    required: ["owner", "repo"],
  },
  async call({ owner, repo }) {
    const o = String(owner ?? "").trim();
    const r = String(repo ?? "").trim();
    if (!o || !r) return { ok: false, error: "owner and repo required" };
    const res = await fetch(`https://api.github.com/repos/${o}/${r}`, {
      headers: { Accept: "application/vnd.github+json" },
    });
    if (!res.ok) return { ok: false, error: `GitHub ${res.status}` };
    const j = (await res.json()) as Record<string, unknown>;
    return {
      ok: true,
      data: {
        fullName: j.full_name,
        description: j.description,
        stars: j.stargazers_count,
        forks: j.forks_count,
        language: j.language,
        pushedAt: j.pushed_at,
        url: j.html_url,
      },
    };
  },
};

const getLatestCommits: MCPTool = {
  name: "getLatestCommits",
  description:
    "Fetch latest commits with author name/email/login and date. Use whenever the user asks about commit authorship — pushedAt from getGithubRepo is repo-level activity, not author attribution.",
  inputSchema: {
    type: "object",
    properties: {
      owner: { type: "string", description: "Repo owner." },
      repo: { type: "string", description: "Repo name." },
      limit: { type: "string", description: "Max commits to return (default 5, max 10)." },
    },
    required: ["owner", "repo"],
  },
  async call({ owner, repo, limit }) {
    const o = String(owner ?? "").trim();
    const r = String(repo ?? "").trim();
    const lim = Math.min(10, Math.max(1, Number(limit ?? 5) || 5));
    if (!o || !r) return { ok: false, error: "owner and repo required" };
    const res = await fetch(
      `https://api.github.com/repos/${o}/${r}/commits?per_page=${lim}`,
      { headers: { Accept: "application/vnd.github+json" } },
    );
    if (!res.ok) return { ok: false, error: `GitHub ${res.status}` };
    const arr = (await res.json()) as Array<Record<string, unknown>>;
    if (!Array.isArray(arr)) return { ok: false, error: "unexpected GitHub response" };
    return {
      ok: true,
      data: {
        commits: arr.slice(0, lim).map((c) => {
          const commit = (c.commit as Record<string, unknown>) ?? {};
          const author = (commit.author as Record<string, unknown>) ?? {};
          const ghAuthor = (c.author as Record<string, unknown> | null) ?? {};
          const msg = typeof commit.message === "string" ? commit.message.split("\n")[0] : null;
          const sha = typeof c.sha === "string" ? c.sha.slice(0, 7) : null;
          return {
            sha,
            message: msg,
            authorName: (author.name as string) ?? null,
            authorEmail: (author.email as string) ?? null,
            authorLogin: (ghAuthor?.login as string) ?? null,
            date: (author.date as string) ?? null,
            url: (c.html_url as string) ?? null,
          };
        }),
      },
    };
  },
};

const getNpmPackage: MCPTool = {
  name: "getNpmPackage",
  description:
    "Fetch live npm package data (latest version, weekly downloads, publish date). Use for packages on npm — e.g. mcp-jest, omnimcp, ensure-json, otterly.",
  inputSchema: {
    type: "object",
    properties: {
      name: { type: "string", description: "npm package name." },
    },
    required: ["name"],
  },
  async call({ name }) {
    const n = String(name ?? "").trim();
    if (!n) return { ok: false, error: "name required" };
    const meta = await fetch(
      `https://registry.npmjs.org/${encodeURIComponent(n).replace("%40", "@")}`,
    );
    if (!meta.ok) return { ok: false, error: `npm registry ${meta.status}` };
    const m = (await meta.json()) as Record<string, unknown>;
    const latest = (m["dist-tags"] as Record<string, string> | undefined)?.latest;
    const versions = m.versions as Record<string, Record<string, unknown>> | undefined;
    const latestVer = latest && versions ? versions[latest] : undefined;
    let weekly: number | null = null;
    try {
      const dl = await fetch(
        `https://api.npmjs.org/downloads/point/last-week/${encodeURIComponent(n).replace("%40", "@")}`,
      );
      if (dl.ok) {
        const d = (await dl.json()) as Record<string, unknown>;
        if (typeof d.downloads === "number") weekly = d.downloads;
      }
    } catch { /* non-fatal */ }
    return {
      ok: true,
      data: {
        name: m.name,
        latestVersion: latest,
        description: (latestVer?.description as string) ?? (m.description as string) ?? null,
        weeklyDownloads: weekly,
        modified: (m.time as Record<string, string> | undefined)?.modified,
      },
    };
  },
};

const getPyPIPackage: MCPTool = {
  name: "getPyPIPackage",
  description: "Fetch PyPI package data (version, summary, author). Use for Python packages, e.g. aa-kit.",
  inputSchema: {
    type: "object",
    properties: { name: { type: "string", description: "PyPI package name." } },
    required: ["name"],
  },
  async call({ name }) {
    const n = String(name ?? "").trim();
    if (!n) return { ok: false, error: "name required" };
    const res = await fetch(`https://pypi.org/pypi/${encodeURIComponent(n)}/json`);
    if (!res.ok) return { ok: false, error: `PyPI ${res.status}` };
    const j = (await res.json()) as Record<string, unknown>;
    const info = (j.info as Record<string, unknown>) ?? {};
    return {
      ok: true,
      data: {
        name: info.name,
        latestVersion: info.version,
        summary: info.summary,
        author: info.author,
        homePage: info.home_page,
      },
    };
  },
};

const getAppStoreApp: MCPTool = {
  name: "getAppStoreApp",
  description:
    "Fetch iOS App Store metadata by numeric app ID. Tongue=6759001870 (us), Lockbox=6747517926 (us), Plum Health Benefits=1616851078 (in).",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "Numeric App Store ID." },
      country: { type: "string", description: "Two-letter country code, default 'us'." },
    },
    required: ["appId"],
  },
  async call({ appId, country }) {
    const id = String(appId ?? "").trim();
    const c = String(country ?? "us").trim().toLowerCase();
    if (!id) return { ok: false, error: "appId required" };
    const res = await fetch(
      `https://itunes.apple.com/lookup?id=${encodeURIComponent(id)}&country=${encodeURIComponent(c)}`,
    );
    if (!res.ok) return { ok: false, error: `iTunes ${res.status}` };
    const j = (await res.json()) as Record<string, unknown>;
    const results = (j.results as Array<Record<string, unknown>>) ?? [];
    if (results.length === 0) return { ok: false, error: "no app found" };
    const app = results[0];
    return {
      ok: true,
      data: {
        name: app.trackName,
        version: app.version,
        averageUserRating: app.averageUserRating,
        userRatingCount: app.userRatingCount,
        primaryGenre: app.primaryGenreName,
        releaseDate: app.releaseDate,
        currentVersionReleaseDate: app.currentVersionReleaseDate,
        url: app.trackViewUrl,
      },
    };
  },
};

const findOpenAIRoles: MCPTool = {
  name: "findOpenAIRoles",
  description:
    "Fetch live OpenAI job postings from Ashby's public API. Filter by keyword (e.g. 'forward deployed', 'startups') and location. Munich auto-excluded (German req).",
  inputSchema: {
    type: "object",
    properties: {
      filter: { type: "string", description: "Title substring filter." },
      location: { type: "string", description: "Location substring filter." },
      limit: { type: "string", description: "Max roles to return (default 6, max 12)." },
    },
    required: [],
  },
  async call({ filter, location, limit }) {
    const f = String(filter ?? "").trim().toLowerCase();
    const loc = String(location ?? "").trim().toLowerCase();
    const lim = Math.min(12, Math.max(1, Number(limit ?? 6) || 6));
    const res = await fetch(
      "https://api.ashbyhq.com/posting-api/job-board/openai?includeCompensation=true",
    );
    if (!res.ok) return { ok: false, error: `Ashby ${res.status}` };
    const d = (await res.json()) as { jobs?: Array<Record<string, unknown>> };
    const matches = (d.jobs ?? [])
      .filter((j) => {
        const t = String(j.title ?? "").toLowerCase();
        const l = String(j.location ?? "").toLowerCase();
        if (f && !t.includes(f)) return false;
        if (loc && !l.includes(loc)) return false;
        if (l.includes("munich") && !loc.includes("munich")) return false;
        return true;
      })
      .slice(0, lim)
      .map((j) => ({
        title: j.title,
        location: j.location,
        team: j.team,
        url: j.jobUrl,
      }));
    return {
      ok: true,
      data: { matchCount: matches.length, roles: matches },
    };
  },
};

const getSubstackPosts: MCPTool = {
  name: "getSubstackPosts",
  description: "Fetch recent posts from Harsh's Substack 'Unengineered' RSS feed.",
  inputSchema: {
    type: "object",
    properties: { limit: { type: "string", description: "Max posts (default 5, max 10)." } },
    required: [],
  },
  async call({ limit }) {
    const lim = Math.min(10, Math.max(1, Number(limit ?? 5) || 5));
    const res = await fetch("https://unengineered.substack.com/feed");
    if (!res.ok) return { ok: false, error: `Substack ${res.status}` };
    const xml = await res.text();
    const posts: Array<{ title: string; link: string; pubDate?: string }> = [];
    const re = /<item>([\s\S]*?)<\/item>/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(xml)) && posts.length < lim) {
      const block = m[1];
      const titleMatch =
        /<title>\s*(?:<!\[CDATA\[)?([\s\S]*?)(?:\]\]>)?\s*<\/title>/i.exec(block);
      const linkMatch = /<link>([\s\S]*?)<\/link>/i.exec(block);
      const dateMatch = /<pubDate>([\s\S]*?)<\/pubDate>/i.exec(block);
      const title = titleMatch?.[1]?.trim();
      const link = linkMatch?.[1]?.trim();
      if (title && link) {
        posts.push({ title, link, pubDate: dateMatch?.[1]?.trim() });
      }
    }
    return { ok: true, data: { posts } };
  },
};

const getResumeSection: MCPTool = {
  name: "getResumeSection",
  description:
    "Return a structured slice of Harsh's resume. Sections: summary, experience, reallyartificial, shipped_apps, skills, education, writing, looking_for, contact.",
  inputSchema: {
    type: "object",
    properties: {
      section: {
        type: "string",
        description: "Which resume section.",
        enum: [
          "summary",
          "experience",
          "reallyartificial",
          "shipped_apps",
          "skills",
          "education",
          "writing",
          "looking_for",
          "contact",
        ],
      },
    },
    required: ["section"],
  },
  async call({ section }) {
    const s = String(section ?? "").trim();
    // Static data, mirrors api/tools/resume.ts but trimmed for browser-side use
    const data: Record<string, unknown> = {
      summary:
        "Forward-deployed AI engineer. Founding engineer at Plum (now 6K companies / 600K lives). Co-founder of an early-stage AI startup. Founder of ReallyArtificial.org (OSS agent infrastructure). Currently at Coupang.",
      experience: [
        "Coupang — Senior SWE (Oct 2025 — Present). Distributed WMS, 100+ FCs.",
        "Early-stage AI startup — Co-founder & FDE (2025, 6 months). Cleartrip, Bharatsure, 100ms, Putting Scene.",
        "Visa (Authorize.Net) — Senior SWE L5 (Mar 2024 — Oct 2025). 500+ TPS, 445K+ merchants, $200B+ annualised.",
        "Qoala — GenAI consultant. Motor-inspection platform (10K+/month).",
        "Plum — Founding SWE (Jan 2021 — Feb 2024). First line of code. Sole engineer on v1 iOS app. Now 6K companies, 600K lives.",
      ],
      looking_for:
        "Open to OpenAI roles in 2026: AI Deployment Engineer, Forward-Deployed Engineer, Solutions Engineer. SF, NYC, Singapore, Sydney, London, Paris, Tokyo, Dublin, India Remote. Skipping Munich (German).",
      contact: {
        email: "harsh.joshi.pth@gmail.com",
        linkedin: "https://linkedin.com/in/josharsh",
        github: "https://github.com/josharsh",
      },
    };
    if (!(s in data)) return { ok: false, error: `unknown section: ${s}` };
    return { ok: true, data: data[s] };
  },
};

const saveLead: MCPTool = {
  name: "saveLead",
  description:
    "WRITE TOOL. Save a visitor's contact + intent so Harsh can follow up. Only call after explicit consent.",
  inputSchema: {
    type: "object",
    properties: {
      email: { type: "string", description: "Visitor email (required)." },
      name: { type: "string", description: "Visitor name (optional)." },
      intent: { type: "string", description: "hire | consult | collaborate | press | fan | other" },
      summary: { type: "string", description: "2-3 sentence summary of what they want." },
    },
    required: ["email", "intent", "summary"],
  },
  async call({ email, name, intent, summary }) {
    const e = String(email ?? "").trim();
    if (!e || !e.includes("@")) return { ok: false, error: "valid email required" };
    // In browser we can't securely write to Supabase without exposing keys.
    // Return a stub success — the trace shows the intent; for real
    // persistence we'd POST to a backend endpoint.
    return {
      ok: true,
      data: {
        saved: false,
        via: "stub",
        note: "no browser-safe backend configured; recommend emailing harsh.joshi.pth@gmail.com",
        email: e,
        name: name ?? "anonymous",
        intent,
        summary,
      },
    };
  },
};

const bookCall: MCPTool = {
  name: "bookCall",
  description:
    "WRITE TOOL. Returns a Cal.com URL or prefilled mailto link for booking time with Harsh.",
  inputSchema: {
    type: "object",
    properties: {
      topic: { type: "string", description: "What they want to discuss." },
      visitorEmail: { type: "string", description: "Their email (optional)." },
    },
    required: ["topic"],
  },
  async call({ topic, visitorEmail }) {
    const t = String(topic ?? "Quick chat").trim();
    const ve = String(visitorEmail ?? "").trim();
    const subject = encodeURIComponent(`[josharsh.com] ${t}`);
    const body = encodeURIComponent(
      `Hi Harsh,\n\nFrom Chief on your site. Topic: ${t}.\n\n— ${ve || "(your name)"}`,
    );
    return {
      ok: true,
      data: {
        kind: "mailto",
        url: `mailto:harsh.joshi.pth@gmail.com?subject=${subject}&body=${body}`,
      },
    };
  },
};

const analyzeJobDescription: MCPTool = {
  name: "analyzeJobDescription",
  description:
    "Fetch and analyse a job description from a URL (Ashby works best) or pasted text.",
  inputSchema: {
    type: "object",
    properties: {
      url: { type: "string", description: "Job posting URL (Ashby, generic HTML)." },
      text: { type: "string", description: "Pasted JD text (if no URL)." },
    },
    required: [],
  },
  async call({ url, text }) {
    const u = String(url ?? "").trim();
    const t = String(text ?? "").trim();
    if (!u && !t) return { ok: false, error: "url or text required" };
    let body = t;
    let title: string | undefined;
    let location: string | undefined;
    if (u) {
      const ashby = u.match(/jobs\.ashbyhq\.com\/([^/]+)\/([0-9a-f-]{36})/i);
      if (ashby) {
        const [, org, jobId] = ashby;
        const res = await fetch(
          `https://api.ashbyhq.com/posting-api/job-board/${org}?includeCompensation=true`,
        );
        if (!res.ok) return { ok: false, error: `Ashby ${res.status}` };
        const d = (await res.json()) as { jobs?: Array<Record<string, unknown>> };
        const job = (d.jobs ?? []).find((j) => j.id === jobId);
        if (!job) return { ok: false, error: "job not found" };
        title = String(job.title ?? "");
        location = String(job.location ?? "");
        body = String(job.descriptionPlain ?? "");
      } else {
        try {
          const res = await fetch(u);
          if (!res.ok) return { ok: false, error: `fetch ${res.status}` };
          body = (await res.text()).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").slice(0, 10000);
        } catch (e) {
          return { ok: false, error: e instanceof Error ? e.message : "fetch failed" };
        }
      }
    }
    return {
      ok: true,
      data: {
        title,
        location,
        bodyPreview: body.slice(0, 1500),
        keyRequirements: body
          .split(/(?<=[.!?])\s+/)
          .filter((s) =>
            /(experience|expertise|you (have|bring|will|'ll)|proficien|familiar with|strong)/i.test(s),
          )
          .slice(0, 8),
      },
    };
  },
};

// ─────────────────────────── registry ───────────────────────────

export const BROWSER_TOOLS: Record<string, MCPTool> = {
  getGithubRepo,
  getLatestCommits,
  getNpmPackage,
  getPyPIPackage,
  getAppStoreApp,
  findOpenAIRoles,
  getSubstackPosts,
  getResumeSection,
  saveLead,
  bookCall,
  analyzeJobDescription,
};

export function listMCPTools(): Array<{
  name: string;
  description: string;
  inputSchema: MCPInputSchema;
}> {
  return Object.values(BROWSER_TOOLS).map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: t.inputSchema,
  }));
}
